import { useEffect, useState } from "react";
import { useEHR } from "../../hooks/useEHR";
import Form from "../common/Form";
import Modal from "../common/Modal";
import { useChats } from "../../hooks/useChats";
import { toast } from "react-toastify";
import { compareVersions } from "compare-versions";
import { useTranslation } from "react-i18next";

// "Patient chart — advancedmd.com". Falls back to the bare title when the URL is
// missing or unparseable (chrome:// pages, extension pages).
function tabOptionLabel(tab) {
  const title = tab?.title || "";
  try {
    const host = new URL(tab.url).hostname;
    if (host) return title ? title + " — " + host : host;
  } catch (e) {
    // No usable URL — fall through to the title alone.
  }
  return title;
}

export default function ModalSelectTabEhr() {
  const { t } = useTranslation();
  const {
    requestTabs,
    showModalSelectTab,
    setShowModalSelectTab,
    executionTab,
    executionChat,
    setExecutionChat,
    setExecutionTab,
    selectedTabPromise,
    version,
  } = useEHR();
  const [tabs, setTabs] = useState([]);
  const { chats } = useChats();
  const [initChat] = useState(
    executionChat
      ? executionChat.id +
          " - " +
          executionChat?.contextVariables?.["local:encounterTypeLabel"] +
          " - " +
          executionChat.userLastName +
          " " +
          executionChat.userFirstName
      : null
  );

  useEffect(() => {
    const fetchTabs = async () => {
      const fetchedTabs = await requestTabs();
      setTabs(fetchedTabs);
    };

    fetchTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = () => {
    if (!executionChat && showModalSelectTab.ignoreChat !== true) {
      return toast.error(t("ehr.selectTab.toast.selectChat"));
    }
    if (!executionTab && compareVersions(version, "1.1.0") >= 0) {
      return toast.error(t("ehr.selectTab.toast.selectTab"));
    }

    if (selectedTabPromise.current) {
      selectedTabPromise.current(true);
    }

    setShowModalSelectTab(false);
  };

  const close = () => {
    setShowModalSelectTab(false);
    if (selectedTabPromise.current) {
      selectedTabPromise.current(false);
    }
  };

  return (
    <Modal title={t("ehr.selectTab.title")} close={close} size={"medium"} show={true} className={"min-height"}>
      <Form
        fields={[
          {
            label: t("ehr.selectTab.chatLabel"),
            type: "autocomplete",
            initValue: initChat,
            value: executionChat,
            required: true,
            onSelect: (e) => setExecutionChat(e?.value),
            show: showModalSelectTab.ignoreChat ? false : true,
            data: chats.map((c) => ({
              key: c.id,
              value: c,
              label:
                c.id + " - " + c.contextVariables["local:encounterTypeLabel"] + " - " + c.userLastName + " " + c.userFirstName,
            })),
          },
          {
            show: compareVersions(version, "1.1.0") >= 0,
            label: t("ehr.selectTab.tabLabel"),
            required: true,
            type: "select",
            value: executionTab,
            options: [
              { label: t("ehr.selectTab.selectTab"), key: "" },
              // Show the host alongside the title: a page title is page-controlled, so
              // the host is the only trustworthy way to tell the real EHR tab from a
              // look-alike — and it also makes several open patient tabs distinguishable.
              ...(tabs ? tabs.map((tab) => ({ label: tabOptionLabel(tab), key: parseInt(tab.id) })) : []),
            ],
            onChange: (e) => setExecutionTab(e.target.value),
          },
        ]}
      />
      <div className="modal-actions">
        <button onClick={exec}>{t("ehr.selectTab.execute")}</button>
      </div>
    </Modal>
  );
}
